import React, { useEffect } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';
import { css } from '@compiled/react';
import { format } from 'date-fns';

import Button from '@atlaskit/button';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	OnboardingWarningBanner,
	BannerLink,
} from '@confluence/banners/entry-points/OnboardingWarningBanner.tsx';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { linkToAdminHubOrg } from '../utils/getAdminHubUrl.ts';

// eslint-disable-next-line import/no-cycle
import { NON_COMPLIANT_BANNER_HEIGHT } from './GracePeriodBanner.tsx';

const i18n = defineMessages({
	deactivationWarning: {
		id: 'trial-components.banner.warning.deactivation',
		defaultMessage:
			'Your trial has ended and you’ve exceeded the user limit. This site will be deactivated on {deactivationDateLong} unless you add billing information to upgrade, or remove {numberOfUsersToDelete, plural, one {# user} other {# users}}.',
		description:
			'inform the user the trial has ended and they exceed the free plan limits. user needs to take action or the site will be deactivated',
	},
	linkToAddBillingV2: {
		id: 'trial-components.banner.add-billing.link.v2',
		defaultMessage: 'Add payment details',
		description: 'CTA link to add payment details',
	},
	linkToLearnMore: {
		id: 'trial-components.banner.learn-more.link',
		defaultMessage: 'Learn more',
		description:
			'Link text that directs users to additional information about trial expiration and account deactivation policies',
	},
	reverseTrialsChooseAPlan: {
		id: 'trial-components.banner.non-compliant.reverse-trials.choose-a-plan',
		defaultMessage:
			'Your trial has ended and you’ve exceeded the Free user limit. <choosePlanLink>Choose a paid plan</choosePlanLink> or <removeUsersLink>remove users</removeUsersLink> to prevent site deactivation on {deactivationDateLong}.',
		description:
			'Inform the user that their trial has ended and they have exceeded the user limit. They need to choose a paid plan or remove users to prevent site deactivation.',
	},
});

const LEARN_MORE_LINK =
	'https://support.atlassian.com/confluence-cloud/docs/removing-users-and-downgrading-to-confluence-free';

interface NonCompliantBannerData {
	linkToAddBilling: string;
	deactivationDate: string;
	numberOfUsersOverFreeLimit: number;
	licenseConsumingUserCount: number;
	edition: string;
	premiumReverseTrialABCohort?: string;
	linkToChoosePlan?: string;
}

const inlineLinkCSS = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&&': {
		padding: 0,
	},
});

export const NonCompliantBannerMessage = ({
	linkToAddBilling,
	deactivationDate,
	numberOfUsersOverFreeLimit,
	licenseConsumingUserCount,
	edition,
	premiumReverseTrialABCohort,
	linkToChoosePlan,
}: NonCompliantBannerData) => {
	const { orgId, environment } = useSessionData();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'gracePeriodBanner',
				attributes: {
					deactivationDate,
					licenseConsumingUserCount,
					edition,
					premiumReverseTrialABCohort,
				},
			},
		}).fire();
	}, [
		createAnalyticsEvent,
		deactivationDate,
		licenseConsumingUserCount,
		edition,
		premiumReverseTrialABCohort,
	]);

	const onBillingClicked = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'gracePeriodBannerBilling',
				source: 'gracePeriodBanner',
				attributes: {
					deactivationDate,
					licenseConsumingUserCount,
					edition,
					premiumReverseTrialABCohort,
				},
			},
		}).fire();
	};

	const onChoosePlanClicked = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'gracePeriodBannerChoosePlanCTA',
				source: 'gracePeriodBanner',
				attributes: {
					deactivationDate,
					licenseConsumingUserCount,
					premiumReverseTrialABCohort,
				},
			},
		}).fire();
	};

	const onRemoveUsersClicked = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'gracePeriodBannerRemoveUsers',
				source: 'gracePeriodBanner',
				attributes: {
					deactivationDate,
					licenseConsumingUserCount,
					edition,
					premiumReverseTrialABCohort,
				},
			},
		}).fire();
	};

	const onDropdownClicked = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'gracePeriodBannerDropDown',
				source: 'gracePeriodBanner',
			},
		}).fire();
	};

	const onLearnMoreClicked = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'gracePeriodBannerLearnMore',
				source: 'gracePeriodBanner',
			},
		}).fire();
	};

	if (premiumReverseTrialABCohort === 'variation') {
		return (
			<OnboardingWarningBanner
				bannerHeight={NON_COMPLIANT_BANNER_HEIGHT}
				description={
					<FormattedMessage
						{...i18n.reverseTrialsChooseAPlan}
						values={{
							deactivationDateLong: format(new Date(deactivationDate), 'MMMM d, yyyy'),
							choosePlanLink: (chunks) => (
								<BannerLink
									href={linkToChoosePlan}
									target="_blank"
									onClick={onChoosePlanClicked}
									css={inlineLinkCSS as any}
								>
									{chunks}
								</BannerLink>
							),
							removeUsersLink: (chunks) => (
								<BannerLink
									href={linkToAdminHubOrg(environment, orgId, '/users?status=ACTIVE')}
									target="_blank"
									onClick={onRemoveUsersClicked}
									css={inlineLinkCSS as any}
								>
									{chunks}
								</BannerLink>
							),
						}}
					/>
				}
			/>
		);
	}

	return (
		<OnboardingWarningBanner
			bannerHeight={NON_COMPLIANT_BANNER_HEIGHT}
			description={
				<FormattedMessage
					{...i18n.deactivationWarning}
					values={{
						deactivationDateLong: deactivationDate,
						numberOfUsersToDelete: numberOfUsersOverFreeLimit,
					}}
				/>
			}
			inlineDescriptionCTA={
				<BannerLink href={linkToAddBilling} target="_blank" onClick={onBillingClicked}>
					<FormattedMessage {...i18n.linkToAddBillingV2} />
				</BannerLink>
			}
			rightSideComponent={
				<DropdownMenu
					trigger={({ triggerRef, ...props }) => (
						<Button
							{...props}
							appearance="subtle"
							iconBefore={<MoreIcon label="more" />}
							ref={triggerRef}
							onClick={(e) => {
								if (props.onClick) {
									props.onClick(e);
								}
								onDropdownClicked();
							}}
						/>
					)}
				>
					<DropdownItemGroup>
						<DropdownItem
							onClick={() => {
								window.open(LEARN_MORE_LINK, '_blank');
								onLearnMoreClicked();
							}}
						>
							<FormattedMessage {...i18n.linkToLearnMore} />
						</DropdownItem>
					</DropdownItemGroup>
				</DropdownMenu>
			}
		/>
	);
};
